import { useCallback, useEffect, useState } from "react";
import { X } from "lucide-react";
import logoImage from "@/assets/newlogo.png";
import skoolPreview from "@/assets/skool-community-preview.webp";
import { usePaymentStatus } from "@/hooks/usePaymentStatus";

// Rotating home-page popup. Each time a visitor closes one, the next one in the
// list shows on a later visit. To add a popup, add an entry here.
type Promo = {
  id: string;
  title: string;
  body: string;
  cta: string;
  href: string; // "http..." opens a new tab; "#..." or "/..." stays on the site
  image?: string;
  imageAlt?: string;
};

const PROMOS: Promo[] = [
  {
    id: "skool",
    title: "AI Automation Base is for people who build their own systems",
    body: "Join our free Skool community for workflows, skills, and direct support at no extra cost.",
    cta: "Join the Skool community — free",
    href: "https://www.skool.com/aiautomation-by-adam-8725/about",
    image: skoolPreview,
    imageAlt: "The AI Automation Base community on Skool",
  },
  {
    id: "packs",
    title: "Build Packs: a complete system on your own machine",
    body: "Each pack installs one working system with Claude Code, one prompt at a time. Your stack, on your machine.",
    cta: "Browse the Build Packs",
    href: "/packs",
  },
  {
    id: "pricing",
    title: "Every lesson, pack and prompt in one place",
    body: "The vault is $20 a month, $50 a year, or $99 once for lifetime access.",
    cta: "See the plans",
    href: "#pricing",
  },
  {
    id: "classroom",
    title: "The Classroom Library, mirrored from Skool every day",
    body: "Follow-along classes on Claude Code and n8n. Built live. Mistakes included.",
    cta: "Open the Classroom",
    href: "/classroom",
  },
  {
    id: "prompts",
    title: "100 prompts written for builders",
    body: "The Prompt Library comes with every plan: prompts for Claude Code, n8n, content and client work.",
    cta: "Get access",
    href: "#pricing",
  },
  {
    id: "maita",
    title: "La Biblioteca de Maita",
    body: "Coloring books and printables for kids and grown-ups.",
    cta: "Visit the library",
    href: "https://labibliotecademaita.com",
  },
  {
    id: "orbix",
    title: "Rather have it built than build it?",
    body: "That's what Orbix is for. Building it for people is my day job; teaching people to build it is what this site is for.",
    cta: "Visit getorbix.com",
    href: "https://getorbix.com",
  },
];

const NEXT_KEY = "promo_popup_next";
const HIDDEN_KEY = "promo_popup_hidden_until";
const HIDE_DAYS = 1;

const readNumber = (key: string) => {
  try {
    const n = Number(localStorage.getItem(key) ?? 0);
    return Number.isFinite(n) ? n : 0;
  } catch {
    return 0;
  }
};

export function PromoPopup() {
  const [promo, setPromo] = useState<Promo | null>(null);
  const { hasAccess, loading: accessLoading } = usePaymentStatus();

  const dismiss = useCallback(() => {
    setPromo(null);
    try {
      const next = (readNumber(NEXT_KEY) + 1) % PROMOS.length;
      localStorage.setItem(NEXT_KEY, String(next));
      localStorage.setItem(HIDDEN_KEY, String(Date.now() + HIDE_DAYS * 24 * 60 * 60 * 1000));
    } catch {
      // Private mode / storage disabled: closing still works for this visit.
    }
  }, []);

  useEffect(() => {
    // Not for people who already bought, nor right after a close, nor for a
    // visitor who landed on #pricing — they came to buy.
    if (accessLoading || hasAccess) return;
    if (readNumber(HIDDEN_KEY) > Date.now()) return;
    if (window.location.hash === "#pricing") return;

    const t = setTimeout(() => {
      // First visits open the sign-in modal, which blocks clicks outside itself:
      // stacking on it would leave this popup impossible to close. Wait for a later visit.
      if (document.querySelector('[role="dialog"]')) return;
      setPromo(PROMOS[readNumber(NEXT_KEY) % PROMOS.length]);
    }, 5000);
    return () => clearTimeout(t);
  }, [accessLoading, hasAccess]);

  useEffect(() => {
    if (!promo) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") dismiss();
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [promo, dismiss]);

  if (!promo) return null;

  const external = promo.href.startsWith("http");

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={promo.title}
      className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/75 backdrop-blur-sm animate-in fade-in duration-200"
      onClick={dismiss}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-[600px] max-h-[92vh] overflow-y-auto rounded-2xl bg-card border border-border shadow-[0_20px_60px_-15px_rgba(0,0,0,0.8)] [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
      >
        <button
          type="button"
          onClick={dismiss}
          aria-label="Close popup"
          className="absolute top-3 right-3 z-10 p-1.5 rounded-full bg-background/80 hover:bg-background border border-border text-foreground transition-colors"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="px-6 pt-7 pb-5 text-center">
          <img
            src={logoImage}
            alt="AI Automation Base"
            width="120"
            height="36"
            className="h-9 w-auto mx-auto mb-4"
          />
          <h2 className="text-xl sm:text-2xl font-bold leading-tight text-foreground">{promo.title}</h2>
          <p className="mt-2 text-sm text-muted-foreground">{promo.body}</p>
        </div>

        {promo.image && (
          <div className="px-5">
            <div className="rounded-xl overflow-hidden border border-border shadow-lg">
              <img src={promo.image} alt={promo.imageAlt ?? ""} loading="lazy" className="w-full h-auto block" />
            </div>
          </div>
        )}

        <div className="p-5 sm:p-6">
          <div className="[perspective:600px]">
            <a
              href={promo.href}
              {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
              onClick={dismiss}
              className="block w-full text-center rounded-xl px-6 py-3.5 text-sm sm:text-base font-extrabold bg-primary text-primary-foreground
                shadow-[0_4px_0_0_hsl(var(--primary)/0.6),0_8px_20px_-4px_hsl(var(--primary)/0.5)]
                hover:-translate-y-1 hover:shadow-[0_6px_0_0_hsl(var(--primary)/0.6),0_12px_24px_-4px_hsl(var(--primary)/0.5)]
                active:translate-y-0.5 active:shadow-[0_2px_0_0_hsl(var(--primary)/0.6),0_4px_10px_-2px_hsl(var(--primary)/0.5)]
                transition-all duration-150 ease-out"
            >
              {promo.cta}
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
